// Local storage utilities for admin dashboard
export class LocalStorageManager {
  static STORAGE_KEYS = {
    PROJECTS: 'helloheman_projects',
    ABOUT: 'helloheman_about',
    NOW: 'helloheman_now',
    HOME_VIDEO: 'helloheman_home_video',
    ADMIN_AUTH: 'helloheman_admin_auth'
  }

  // Generic storage methods
  static setItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value))
      return true
    } catch (error) {
      console.error('Error saving to localStorage:', error)
      return false
    }
  }

  static getItem(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      console.error('Error reading from localStorage:', error)
      return defaultValue
    }
  }

  static removeItem(key) {
    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error('Error removing from localStorage:', error)
      return false
    }
  }

  // Projects management
  static getProjects() {
    return this.getItem(this.STORAGE_KEYS.PROJECTS, [])
  }

  static saveProjects(projects) {
    return this.setItem(this.STORAGE_KEYS.PROJECTS, projects)
  }

  static addProject(project) {
    const projects = this.getProjects()
    const newProject = {
      ...project,
      id: project.id || `project_${Date.now()}`,
      createdAt: project.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    projects.push(newProject)
    this.saveProjects(projects)
    return newProject
  }

  static updateProject(projectId, updates) {
    const projects = this.getProjects()
    const index = projects.findIndex(p => p.id === projectId)
    if (index !== -1) {
      projects[index] = {
        ...projects[index],
        ...updates,
        updatedAt: new Date().toISOString()
      }
      this.saveProjects(projects)
      return projects[index]
    }
    return null
  }

  static deleteProject(projectId) {
    const projects = this.getProjects()
    const filteredProjects = projects.filter(p => p.id !== projectId)
    this.saveProjects(filteredProjects)
    return true
  }

  // About page management
  static getAboutData() {
    return this.getItem(this.STORAGE_KEYS.ABOUT, {
      title: 'A Glimpse About Me',
      subtitle: 'Professional background & expertise',
      content: {
        text: 'Heman Shakthi Mohan Udaiyar\n\nFast learner, curious builder, always shipping. 🚀\n\nGrowth happens outside the comfort zone. Started with a front desk job at 19, moved into freelance work, and learned about real-world projects and deadlines.\n\nDevelopment is my true zone. I wanted to understand the full lifecycle of an app - hosting, scaling, staying online during peak load. This led me to learn infrastructure, server management, and DevOps.\n\nAt Media.net, I reached a high level of problem-solving, automating tasks, building monitoring tools, and creating a custom app for network teams. Kept the "dev spirit alive" in an "ops-heavy environment."\n\nNow I\'m confident in building, deploying, scaling, and supporting real-world applications. But more importantly, I\'m still learning, still vibing, and still shipping.\n\nOh, and I probably did like 100 other cool things I totally forgot to write down and will add later.'
      },
      updatedAt: new Date().toISOString()
    })
  }

  static saveAboutData(aboutData) {
    const data = {
      ...aboutData,
      updatedAt: new Date().toISOString()
    }
    return this.setItem(this.STORAGE_KEYS.ABOUT, data)
  }

  // Now page management
  static getNowData() {
    return this.getItem(this.STORAGE_KEYS.NOW, {
      title: "what i'm doing now",
      content: "Currently working on exciting projects and always open to new opportunities.",
      updatedAt: new Date().toISOString()
    })
  }

  static saveNowData(nowData) {
    const data = {
      ...nowData,
      updatedAt: new Date().toISOString()
    }
    return this.setItem(this.STORAGE_KEYS.NOW, data)
  }

  // Home video management
  static getHomeVideoData() {
    return this.getItem(this.STORAGE_KEYS.HOME_VIDEO, {
      videoUrl: '/HimachalPradesh-fx.mp4',
      posterUrl: '/HimachalPradesh-fx.mp4',
      autoplay: true,
      loop: true,
      muted: true,
      updatedAt: new Date().toISOString()
    })
  }

  static saveHomeVideoData(videoData) {
    const data = {
      ...videoData,
      updatedAt: new Date().toISOString()
    }
    return this.setItem(this.STORAGE_KEYS.HOME_VIDEO, data)
  }

  // Admin authentication
  static setAdminAuth(authData) {
    return this.setItem(this.STORAGE_KEYS.ADMIN_AUTH, authData)
  }

  static getAdminAuth() {
    return this.getItem(this.STORAGE_KEYS.ADMIN_AUTH, null)
  }

  static clearAdminAuth() {
    return this.removeItem(this.STORAGE_KEYS.ADMIN_AUTH)
  }

  // Check if admin is authenticated
  static isAdminAuthenticated() {
    const auth = this.getAdminAuth()
    if (!auth) return false
    
    // Check if session is still valid (24 hours)
    const now = new Date().getTime()
    const authTime = new Date(auth.timestamp).getTime()
    const twentyFourHours = 24 * 60 * 60 * 1000
    
    return (now - authTime) < twentyFourHours
  }

  // Initialize default data if not exists
  static initializeDefaultData() {
    // Initialize projects with some default data
    if (this.getProjects().length === 0) {
      const defaultProjects = [
        {
          id: '1',
          title: 'Ekatva Festival 2024 - Brand Campaign',
          description: 'A vibrant showcase of cultural unity through the lens of the annual Ekatva Festival. This project captures the essence of community, tradition, and celebration in a visually stunning narrative.',
          category: 'video-editing',
          icon: '🎬',
          heroImage: {
            url: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&h=600&fit=crop&crop=center',
            alt: 'Ekatva Festival 2024 Brand Campaign',
          },
          slug: 'ekatva-festival-2024',
          featured: true,
          order: 1,
          client: 'Ekatva Festival',
          projectUrl: '/HimachalPradesh-fx.mp4',
          technologies: [
            { technology: 'Adobe Premiere Pro' },
            { technology: 'After Effects' },
            { technology: 'DaVinci Resolve' },
            { technology: 'Adobe Audition' },
          ],
          projectDate: '2024-01-15',
          duration: '2:15',
          publishedAt: '2024-01-15T00:00:00.000Z',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '2',
          title: 'Veplants Protein Powder Website',
          description: 'A comprehensive e-commerce website for a premium plant-based protein powder brand. Features product showcase, nutritional information, customer reviews, subscription options, and seamless checkout experience.',
          category: 'web-development',
          icon: '🛒',
          heroImage: {
            url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop&crop=center',
            alt: 'Veplants Protein Powder Website',
          },
          slug: 'veplants-website',
          featured: true,
          order: 2,
          client: 'Veplants',
          projectUrl: 'https://veplants.com',
          technologies: [
            { technology: 'React.js' },
            { technology: 'Next.js' },
            { technology: 'Shopify' },
            { technology: 'Stripe API' },
            { technology: 'Tailwind CSS' },
          ],
          projectDate: '2024-06-18',
          duration: '3 months',
          publishedAt: '2024-06-18T00:00:00.000Z',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ]
      this.saveProjects(defaultProjects)
    }
  }
}
